import PropTypes from 'prop-types';
import { useFeatureToggle } from '../utils/hooks/useFeatureToggle';
import Loading from '../views/common/Loading';
import NotFoundView from '../views/errors/NotFoundView';
import ProtectedRoute from './ProtectedRoute';

/**
 * A route component that conditionally renders based on feature toggle state.
 * When the feature is disabled, it renders a 404 page instead of the component.
 * 
 * @param {string} featureName - The name of the feature toggle to check
 * @param {React.ComponentType} component - The component to render when feature is enabled
 * @param {Object} rest - Additional props passed to ProtectedRoute
 * @returns {React.Element} ProtectedRoute with conditional component rendering
 */

const FeatureToggleRoute = ({ featureName, component: Component, ...rest }) => {
  const { isLoading, isFeatureEnabled } = useFeatureToggle(featureName);

  // While loading feature flags, show loading component
  // This prevents showing the component briefly before potentially showing NotFound
  if (isLoading !== false) {
    return <ProtectedRoute {...rest} component={Loading} />;
  }

  // Feature is enabled, render the original component
  if (isFeatureEnabled === true) {
    return <ProtectedRoute {...rest} component={Component} />;

  }

  // If feature is disabled, render 404 page
  return <ProtectedRoute {...rest} component={NotFoundView} />;
};

FeatureToggleRoute.propTypes = {
  featureName: PropTypes.string.isRequired,
  component: PropTypes.elementType.isRequired,
};

export default FeatureToggleRoute;